import * as dateMath from 'date-arithmetic';
import * as React from 'react';
import { Navigate } from 'react-router-dom';
import { rng } from '../../amorg-common';
import { ActionPlanListView } from '../../amorg-common/DTOs/ActionPlanListView';
import { createActivityAndActionPlan, getActionPlanList } from '../../api/actionPlanClientApi';
import { Button } from '../../designSystem/button/Button';
import { LoadingPageOverlay } from '../../designSystem/LoadingPageOverlay';
import { PageHeader } from '../../designSystem/PageHeader';
import { PageSection } from '../../designSystem/PageSection';
import { P } from '../../designSystem/typography/Body';
import { clientAccessHelper } from '../../utils/clientAccessHelper';
import { ActionPlanListingPageSectionTable } from '../_components/activities/ActionPlanListingPageSectionTable';
import { ProtectedPageContext } from '../_contexts/ProtectedPageContext';
import { SingleColumnLayout } from '../_layouts/SingleColumnLayout';
import { UserPageWrapper } from '../_pageWrappers/UserPageWrapper';

type State = {
    activities: ActionPlanListView[],
};

export function ActionPlanListPage (): JSX.Element
{
    return (
        <UserPageWrapper selectedMenuItemKey="actionplans" breadCrumbs={[{ text: 'Handleplaner', url: '' }]}>
            <ActionPlanListPageContent />
        </UserPageWrapper>
    );
}

function ActionPlanListPageContent (): JSX.Element
{
    const activeUser = React.useContext(ProtectedPageContext).user;
    const hasAccess = clientAccessHelper.hasAllPermissions(activeUser, ['ViewAssignedActionPlans']);
    const canCreate = clientAccessHelper.hasAllPermissions(activeUser, ['EditAllActionPlans']);

    const [state, setState] = React.useState<State>({
        activities: []
    });
    const [loading, setLoading] = React.useState(true);
    const [redirectId, setRedirectId] = React.useState('');

    React.useEffect(() =>
    {
        const getData = async () =>
        {
            const actionPlans = await getActionPlanList();
            setState({ activities: actionPlans });
            setLoading(false);
        };
        getData();
    }, []);

    async function handleClickNewActionPlan ()
    {
        setLoading(true);

        const now = new Date();
        const tomorrow = new Date(now.getTime() + 1000 * 60 * 60 * 24); // Add one day
        tomorrow.setHours(8, 0, 0, 0);

        const end = dateMath.add(tomorrow, 1, 'month');
        end.setHours(16, 0, 0, 0);

        const activityId = rng.getRandomId();
        const actionPlanId = rng.getRandomId();

        const createActionPlanResult = await createActivityAndActionPlan(actionPlanId, activityId, { type: 'Independent' }, 'Ny handleplan', tomorrow, end);
        if (!createActionPlanResult.isSuccess)
        {
            console.error('Failed to create action plan:', createActionPlanResult.errorMessage);
        }
        else
        {
            setLoading(false);
            setRedirectId(actionPlanId);
        }
    }

    if (!hasAccess)
        return <P>Hov! Du har ikke rettigheder til at tilgå denne side.</P>;

    const now = new Date();
    const current = state.activities.filter(a => a.end > now);
    const past = state.activities.filter(a => a.end <= now);

    return (
        <>
            <LoadingPageOverlay loading={loading} />

            <PageHeader
                headerText="Handleplaner"
                headerIcon="list_alt_check"
                buttons={canCreate &&
                    <Button variant="filled" icon="actions_plus" size={"32px"} emphasize text="Opret ny" onClick={handleClickNewActionPlan} />
                }
            />

            <SingleColumnLayout>
                <>
                    {redirectId !== '' &&
                        <Navigate to={`/actionplans/${redirectId}`} />
                    }
      
                    <PageSection
                        header={{
                            title: 'Aktuelle Handleplaner',
                            icon:'list_alt',
                            defaultCollapsed:false
                        }}
                        headerStyleVariant='minimal'
                        contentStyleVariant='minimal'
                        contentPadding='16px 0'
                    >
                        <ActionPlanListingPageSectionTable 
                            items={current}
                            noContentText="Ingen aktuelle handleplaner"
                            onClick={(actionPlanId) => setRedirectId(actionPlanId)} />

                    </PageSection>

                    

                    {past.length > 0 &&

                        <PageSection
                            header={{
                                title: 'Tidligere Handleplaner',
                                icon:'list_alt_check',
                                defaultCollapsed:false
                            }}
                            headerStyleVariant='minimal'
                            contentStyleVariant='minimal'
                            contentPadding='16px 0'
                        >
                            <ActionPlanListingPageSectionTable 
                                items={past} 
                                noContentText="Ingen tidligere handleplaner"
                                onClick={(actionPlanId) => setRedirectId(actionPlanId)} />

                        </PageSection>

                    }
                </>
            </SingleColumnLayout>
        </>
    );
}
